export interface Contact {
    contatoId: number;
    nome: string;
    telefone: string;
    email: string | null;
    dataCriacao?: string;
    segment?: string | null;
    cep?: string | null;
    street?: string | null;
    number?: string | null;
    complement?: string | null;
    neighborhood?: string | null;
    city?: string | null;
    state?: string | null;
    /** Organização (empresa cliente). Nulo é estado válido. */
    organizacaoId?: number | null;
    /** Vem preenchido da API, para a lista e o painel não consultarem de novo. */
    organizacaoNome?: string | null;
    /** Fora do funil: as conversas ficam no Chat, mas não contam na Pipeline nem nas métricas. Só muda por `definirFunil`. */
    foraDoFunil?: boolean;
}

export interface ApiResponse<T> {
    sucesso: boolean;
    mensagem: string;
    dados: T;
}

export interface CreateContactDTO {
    nome: string;
    telefone: string;
    email: string;
    segment?: string | null;
    cep?: string | null;
    street?: string | null;
    number?: string | null;
    complement?: string | null;
    neighborhood?: string | null;
    city?: string | null;
    state?: string | null;
    organizacaoId?: number | null;
}

export interface UpdateContactDTO {
    contatoId: number;
    nome: string;
    telefone: string;
    email: string;
    segment?: string | null;
    cep?: string | null;
    street?: string | null;
    number?: string | null;
    complement?: string | null;
    neighborhood?: string | null;
    city?: string | null;
    state?: string | null;
    organizacaoId?: number | null;
}

import { apiFetch } from './api';

// ... (interfaces)

const API_URL = import.meta.env.VITE_API_URL || '';

/** Resposta de `Contato/VerificarDuplicidade`. Telefone repetido bloqueia; e-mail repetido só avisa. */
export interface DuplicidadeContato {
    bloqueioPorTelefone: Contact | null;
    avisoPorEmail: Contact[];
}

/** O `mensagem` do corpo de um 400, ou o padrão — corpo vazio (404 de API velha) não vira erro de parser. */
const mensagemDaApi = async (response: Response, padrao: string): Promise<string> => {
    try {
        const corpo = JSON.parse(await response.text());
        return typeof corpo?.mensagem === 'string' && corpo.mensagem.trim() ? corpo.mensagem : padrao;
    } catch {
        return padrao;
    }
};

export const ContactService = {
    /** Tira o contato do funil (ou o devolve). A API também desliga o robô e supera as tarefas padrão ao tirar. */
    definirFunil: async (id: number, foraDoFunil: boolean): Promise<void> => {
        const response = await apiFetch(`${API_URL}/api/Contato/${id}/Funil`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ foraDoFunil }),
        });
        if (!response.ok) throw new Error(await mensagemDaApi(response, 'Não foi possível alterar o funil do contato.'));
    },

    verificarDuplicidade: async (
        telefone: string,
        email: string,
        ignorarId?: number,
    ): Promise<DuplicidadeContato> => {
        const params = new URLSearchParams();
        if (telefone) params.set('telefone', telefone);
        if (email) params.set('email', email);
        if (ignorarId) params.set('ignorarId', String(ignorarId));
        const response = await apiFetch(`${API_URL}/api/Contato/VerificarDuplicidade?${params.toString()}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error(await mensagemDaApi(response, 'Erro ao verificar duplicidade'));
        const corpo = JSON.parse(await response.text());
        return corpo?.dados ?? { bloqueioPorTelefone: null, avisoPorEmail: [] };
    },

    getAll: async (): Promise<ApiResponse<Contact[]>> => {
        const response = await apiFetch(`${API_URL}/api/Contato/BuscarTodos`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            if (response.status === 400) {
                return { sucesso: true, mensagem: 'Sem contatos', dados: [] };
            }
            throw new Error('Erro ao buscar contatos');
        }
        return response.json();
    },

    getById: async (id: number): Promise<ApiResponse<Contact>> => {
        const response = await apiFetch(`${API_URL}/api/Contato/BuscarPorId/${id}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            throw new Error('Erro ao buscar contato');
        }
        return response.json();
    },

    create: async (contact: CreateContactDTO): Promise<ApiResponse<any>> => {
        const response = await apiFetch(`${API_URL}/api/Contato/Criar`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(contact),
        });
        if (!response.ok) {
            // A API explica a recusa (ex.: telefone que já é de outro contato) no `mensagem`.
            throw new Error(await mensagemDaApi(response, 'Erro ao criar contato'));
        }
        return response.json();
    },

    update: async (contact: UpdateContactDTO): Promise<ApiResponse<any>> => {
        const response = await apiFetch(`${API_URL}/api/Contato/Atualizar`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(contact),
        });
        if (!response.ok) {
            // A API explica a recusa (ex.: telefone que já é de outro contato) no `mensagem`.
            throw new Error(await mensagemDaApi(response, 'Erro ao atualizar contato'));
        }
        return response.json();
    },

    delete: async (id: number): Promise<ApiResponse<any>> => {
        const response = await apiFetch(`${API_URL}/api/Contato/Remover/${id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            throw new Error('Erro ao remover contato');
        }
        return response.json();
    },
};
